<template>
  <div :class="`appointment__summary ${showSummary ? 'isOpen' : 'isClose'}`">
    <p class="appointment__summary-title subtitle-2 font-weight-bold mb-3">
      {{ $t('appointment.formCreate.summary.title') }}
    </p>

    <div v-if="appointmentFields.type">
      <div
        v-if="shouldShowTotalPrice"
        class="appointment__summary-amount"
      >
        <v-card class="pa-4 mb-4 green lighten-4 green--text" elevation="0">
          <p class="mb-0 caption font-weight-bold">
            {{ $t('appointment.formCreate.titles.totalAmount') }}
          </p>
          <p class="mb-0 headline">
            {{ formatMoney(appointmentAmount) }}
          </p>
        </v-card>
      </div>

      <div v-if="appointmentFields.type" class="appointment__summary-service">
        <p class="mb-0 caption font-weight-bold">
          {{ $t('appointment.formCreate.summary.serviceAndBranch') }}
        </p>
        <p class="mb-0 body-2">
          {{ $t(`type.${appointmentFields.type}`) }}
        </p>
        <p
          v-if="appointmentFields.real_estate_branch_name"
          class="body-2"
          :class="{ 'mb-0': appointmentFields.inspector_id }"
        >
          {{
            `${$t('appointment.formCreate.summary.branch')}: ${
              appointmentFields.real_estate_branch_name
            }`
          }}
        </p>

      <div v-if="selectedWorker" class="mt-2">
        <p class="mb-0 caption font-weight-bold">
          {{ workerLabel }}
        </p>
          <p class="mb-0 body-2">
            {{ selectedWorker.name }}
          </p>
          <p v-if="selectedWorkerEmail" class="mb-0 caption grey--text text--darken-2">
            {{ selectedWorkerEmail }}
          </p>
          <p v-if="appointmentFields.worker.branch" class="mb-0 caption grey--text text--darken-2">
            {{ appointmentFields.worker.branch.name }}
          </p>
        </div>
      </div>

      <div
        v-if="appointmentFields.date && appointmentFields.date.length >= 11"
        class="appointment__summary-date"
      >
        <v-divider class="my-3" />
        <p class="mb-0 caption font-weight-bold">
          {{ $t('appointment.formCreate.summary.dateTime') }}
        </p>
        <p class="body-2 mb-0">
          {{ formatDate(appointmentFields.date) }}{{ endTimeSummary ? ` ~ ${endTimeSummary}` : '' }}
        </p>
        <div v-if="isUrgent" class="d-flex align-center mt-1">
          <v-chip
            class="pulse-effect mr-2"
            x-small
            color="error"
          >
            {{ $t('appointment.formCreate.summary.urgent') }}
          </v-chip>
          <InformationTooltip
            :message="$t('appointment.formCreate.summary.urgencyDescription')"
            small
          />
        </div>

        <div v-if="hasPeriod">
          <p class="caption font-weight-bold mt-2 mb-0">
            {{ $t('appointment.formCreate.summary.period') }}
          </p>
          <p class="body-2 mb-0">
            {{ periodLabel }}
          </p>
        </div>
      </div>

      <div
        v-if="appointmentFields.realties_complete.length >= 1"
        class="appointment__summary-address"
      >
        <v-divider class="my-3" />

        <v-progress-linear
          v-if="isRealtyListLoading"
          height="2"
          class="mt-n1"
          indeterminate
          absolute
        />

        <p class="mb-n1 caption font-weight-bold">
          {{ $t('appointment.formCreate.summary.realties') }}
        </p>
        <v-list class="transparent" dense>
          <v-list-item
            v-for="realty in appointmentFields.realties_complete"
            :key="realty.id"
            class="px-0"
          >
            <span
              :class="{
                'mb-2': appointmentFields.realties_complete.length >= 2,
              }"
              class="body-2 grey--text text--darken-2"
            >
              <v-tooltip
                v-if="!realty.available || realty.private_area_meters === null"
                top
              >
                <template v-slot:activator="{ on, attrs }">
                  <span
                    v-bind="attrs"
                    v-on="on"
                    class="d-flex align-baseline red--text"
                  >
                    <v-icon color="red lighten-2" small left>
                      mdi-alert-circle
                    </v-icon>
                    <span class="body-2">
                      {{ realty.address }}
                    </span>
                  </span>
                </template>
                <span v-if="!realty.available" class="caption">
                  {{ $t('realty.warnings.realtyNotAvailable') }}
                </span>
                <span
                  v-if="realty.private_area_meters === null"
                  class="caption"
                >
                  {{ $t('realty.warnings.withoutPrivateArea') }}
                </span>
              </v-tooltip>
              <span class="body-2" v-else>
                {{ realty.address }}
              </span>
            </span>
          </v-list-item>
        </v-list>
      </div>

      <div
        v-if="appointmentFormStepsCurr >= 4"
        class="appointment__summary-key-location"
      >
        <v-divider class="my-3" />
        <p class="mb-0 caption font-weight-bold">
          {{ $t('appointment.formCreate.summary.keyLocation') }}
        </p>
        <v-row no-gutters>
          <v-col>
            <p class="mb-0 body-2">
              <small class="d-block">
                {{ $t('appointment.formCreate.labels.summaryKeyPicker') }}
              </small>
              {{
                appointmentFields.key_location_options &&
                appointmentFields.key_location_options.key_picker
                  ? $t(
                      `appointment.keyPicker.${appointmentFields.key_location_options.key_picker}`
                    )
                  : '---'
              }}
            </p>
          </v-col>
          <v-col>
            <p class="mb-0 body-2">
              <small class="d-block">
                {{ $t('appointment.formCreate.labels.summaryKeyLocation') }}
              </small>
              {{
                appointmentFields &&
                appointmentFields.key_location_options &&
                $t(
                  `appointment.meetingLocation.${appointmentFields.key_location_options.key_location}`
                )
              }}
            </p>
          </v-col>
        </v-row>
        <p class="mt-2 mb-0 body-2">
          {{
            appointmentFields.key_location_address
              ? appointmentFields.key_location_address
              : '---'
          }}
        </p>
      </div>

      <div
        v-if="
          appointmentFormStepsCurr >= 4 &&
          appointmentFields.meeting_location_address &&
          hasMeetingLocation
        "
        class="appointment__summary-location"
      >
        <v-divider class="my-3" />
        <p class="mb-0 caption font-weight-bold">
          {{ $t('appointment.formCreate.summary.meetingLocation') }}
        </p>
        <p class="mb-1 body-2">
          {{ appointmentFields.meeting_location_address }}
        </p>
      </div>

      <div
        v-if="appointmentFields.visitor_name"
        class="appointment__summary-key-location"
      >
        <v-divider class="my-3" />
        <p class="mb-0 caption font-weight-bold">
          {{
            isVisit
              ? $t('appointment.formCreate.titles.visitor')
              : $t('appointment.formCreate.titles.tenant')
          }}
        </p>
        <p class="mb-1 body-2">
          {{ appointmentFields.visitor_name }}
        </p>
        <p class="mb-1 body-2">
          {{ appointmentFields.visitor_mail }}
        </p>
        <p class="mb-1 body-2">
          {{
            appointmentFields.visitor_phone &&
            appointmentFields.visitor_phone.length >= 10
              ? formatPhone(appointmentFields.visitor_phone)
              : ''
          }}
        </p>

        <v-row no-gutters>
          <v-col v-if="appointmentFields.visitor_document" cols="8">
            <p class="mb-0 body-2">
              <small class="d-block">
                {{ $t('appointment.formCreate.labels.visitorDocument') }}
              </small>
              {{ formatDocument(appointmentFields.visitor_document) }}
            </p>
          </v-col>
          <v-col
            v-if="
              appointmentFields.visitor_birth_date &&
              appointmentFields.visitor_birth_date.length >= 21
            "
            cols="4"
          >
            <p class="mb-0 body-2">
              <small class="d-block">
                {{ $t('appointment.formCreate.labels.visitorBirthDate') }}
              </small>
              {{
                appointmentFields.visitor_birth_date &&
                !simpleFormatDate(
                  appointmentFields.visitor_birth_date
                ).includes('undefined')
                  ? simpleFormatDate(appointmentFields.visitor_birth_date)
                  : '---'
              }}
            </p>
          </v-col>
        </v-row>
      </div>

      <div v-if="appointmentFormStepsCurr === appointmentFormStepsTotal">
        <div
          v-if="
            appointmentFields.displacement ||
            appointmentFields.needs_authorization ||
            appointmentFields.fci
          "
          class="appointment__summary-other-details"
        >
          <v-divider class="my-3" />
          <p class="mb-0 caption font-weight-bold">
            {{ $t('appointment.formCreate.titles.otherDetails') }}
          </p>
          <p v-if="appointmentFields.displacement" class="mb-0 body-2">
            {{
              $t('appointment.formCreate.labels.displacement', {
                entityName: formatMoney(appointmentFields.displacement),
              })
            }}
          </p>
          <p v-if="appointmentFields.fci" class="mb-0 body-2">
            {{
              $t('appointment.formCreate.labels.fci', {
                entityName: formatMoney(appointmentFields.fci),
              })
            }}
          </p>
          <p class="body-2">
            {{ $t('appointment.formCreate.labels.needsAuthorization') }}
            {{
              appointmentFields.needs_authorization
                ? $t('appointment.formCreate.labels.yesNeedsAuthorization')
                : $t('appointment.formCreate.labels.noNeedsAuthorization')
            }}
          </p>
        </div>
      </div>
    </div>

    <div v-else>
      <p class="mb-0 body-2">
        {{ $t('appointment.formCreate.summary.description') }}
      </p>
    </div>

    <v-btn
      @click="showSummary = !showSummary"
      class="appointment__expandButton"
      color="primary"
      elevation="0"
      overlap
      small
      dark
    >
      <v-icon v-if="showSummary" small> mdi-arrow-right </v-icon>
      <v-icon v-else small left> mdi-arrow-left </v-icon>
      <span v-if="!showSummary" class="caption">
        {{ $t('appointment.buttons.showSummary') }}
      </span>
    </v-btn>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import { formatDocument } from '@/util/document'
import { catalogDate, catalogPhone, catalogMonetary } from '@/util/catalog'
import { workerType as workerTypes } from '@/util/enums'
import InformationTooltip from '../app/InformationTooltip.vue'
import appointment from '../../store/modules/appointment'

const INTERNAL_WORKER_TYPE =
  workerTypes.find(({ value }) => value === 'INTERNAL')?.value || 'INTERNAL'

export default {
  name: 'AppointmentSummary',

  props: {
    hasMeetingLocation: {
      type: Boolean,
      default: false,
    },

    isVisit: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    InformationTooltip,
  },

  data() {
    return {
      amount: 0,
      showSummary: false,
      formatDate: catalogDate,
      formatPhone: catalogPhone,
      formatDocument,
      formatMoney: catalogMonetary,
    }
  },

  computed: {
    ...mapGetters([
      'appointmentFields',
      'appointmentAmount',
      'appointmentFormStepsCurr',
      'appointmentFormStepsTotal',
      'isRealtyListLoading',
      'currentServiceContract',
      'user',
      'currentRealEstate',
      'serviceProviderUsersForAppointment',
    ]),

    selectedWorker() {
      if (this.appointmentFields?.worker?.name) return this.appointmentFields.worker

      const id = this.appointmentFields?.service_provider_user_id
      if (!id) return null

      return this.serviceProviderUsersForAppointment?.find(u => u.id === id) || null
    },

    selectedWorkerEmail() {
      return (
        this.selectedWorker?.email ||
        this.selectedWorker?.mail ||
        this.selectedWorker?.user_email ||
        null
      )
    },

    selectedWorkerBranchName() {
      return this.selectedWorker?.branch?.name || null
    },

    workerLabel() {
      const t = this.$t('appointment.formCreate.labels.serviceProviderUser')
      return typeof t === 'string' && t.includes('.') ? 'Trabalhador' : t
    },

    isUrgent() {
      const realEstate = this.user?.real_estate
      const isCustomerWithoutSubscription = [
        'SINGLE_PURCHASE',
        'PREPAID',
        'PREPAID_SAAS',
      ].includes(realEstate?.subscription_type)

      let hoursMargin = isCustomerWithoutSubscription ? 24 : 16

      const { urgency_tax, urgency_hours_limit } =
        this.currentServiceContract || {}

      if (urgency_tax > 0 && urgency_hours_limit > 0) {
        hoursMargin = urgency_hours_limit
      }

      const dateWithMargin = new Date()
      dateWithMargin.setHours(dateWithMargin.getHours() + hoursMargin)

      const appointmentDate = new Date(this.appointmentFields.date)

      return appointmentDate < dateWithMargin
    },

    useCustomWorkerType() {
      return this.currentRealEstate.use_custom_worker_type
    },

    isInternalWorker() {
      return this.appointmentFields.worker_type === INTERNAL_WORKER_TYPE
    },

    shouldShowTotalPrice() {
      return this.appointmentAmount && !this.useCustomWorkerType && !this.isInternalWorker
    },

    hasWorkerType() {
      return this.appointmentFields.worker_type && this.appointmentFields.worker_type !== 'HOST'
    },

    hasWorker() {
      return this.appointmentFields.worker && this.appointmentFields.worker.name
    },

    hasPeriod() {
      return this.appointmentFields.period_key !== null
    },

    periodLabel() {
      return this.appointmentFields.period_key
        ? this.$t(`appointment.formCreate.labels.periods.${this.appointmentFields.period_key}`)
        : ''
    },

    endTimeSummary() {
      const { date, slot_size } = this.appointmentFields
      if (!date || !slot_size) return null
      return moment(date).add(slot_size, 'minutes').format('HH:mm')
    },
  },

  methods: {
    simpleFormatDate(date) {
      const splittedDateFull = date.split('T')
      const splittedDate = splittedDateFull[0].split('-')
      return `${splittedDate[2]}/${splittedDate[1]}/${splittedDate[0]}`
    },
  },
}
</script>

<style scoped>
.pulse-effect {
  animation: pulse 1.5s ease-out infinite;
  border-radius: 50% !important;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0px rgb(255, 0, 0, 1);
  }
  100% {
    box-shadow: 0 0 0 3px rgba(255, 0, 0, 0);
  }
}
</style>
